/* @jsx h */
import {
  computed,
  defineComponent,
  ref,
  h,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { setTitle } from "@lemmonhq/orders";
import {
  SectionedBody,
  type SectionedBodySection,
  useLocalization,
} from "@lemmonhq/uikit";
import { api } from "@app/data/api";
import {
  RestaurantSettingKey,
  type RestaurantSetting,
  type ReservationServicesSettings,
  type ReservationSchedule,
  type MenuPdfItem,
} from "@lemmonhq/types";
import {
  parseBoolean,
  parseNumber,
  parseReservationServices,
  parseReservationSchedule,
  parseJsonValue,
  parseMenuPdfs,
  getSettingValue,
  createSetting,
} from "@app/data/helpers/settings_helpers";
import { useSettingsForm } from "../../shared/useSettingsForm";
import { GeneralSettings } from "./GeneralSettings";
import { BookingRules } from "./BookingRules";
import { ServicePeriods } from "./ServicePeriods";
import { CustomerMessaging } from "./CustomerMessaging";
import { PaymentDeposits } from "./PaymentDeposits";
import { MenuPdfs } from "./MenuPdfs";
import { TagManagement } from "./TagManagement";
import { GoogleReviews } from "./GoogleReviews";
import { ReviewRequest } from "./ReviewRequest";
import type {
  ReservationSettingsFormState,
  ReservationSettingsUpdateHandler,
} from "@lemmonhq/types";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { useAuth } from "@app/vue/providers/Auth";
import { onBeforeRouteLeave } from "vue-router";
import { createMenuPdfAssetLifecycle } from "./menuPdfAssetLifecycle";

type ReservationSectionProps = {
  title: string;
  icon: string;
};

const ReservationSection = defineComponent<ReservationSectionProps>({
  name: "ReservationSection",
  props: ["title", "icon"],
  setup(props, { slots }) {
    return () => (
      <div class="rounded-lg bg-base-100 shadow-sm l-box-shadow">
        <div class="flex items-center gap-2 px-4 pt-4 text-xl font-semibold text-accent">
          <span class="material-symbols-outlined text-primary">
            {props.icon}
          </span>
          {props.title}
        </div>
        <div class="p-4">{slots.default?.()}</div>
      </div>
    );
  },
});

// Helper to get setting value from settings array
const getSetting = (settings: RestaurantSetting[], key: string): any => {
  return getSettingValue(settings, key);
};

// Helper to parse settings into form state
const parseSettings = (settings: RestaurantSetting[]) => {
  return {
    reservations_enabled: parseBoolean(
      getSetting(settings, RestaurantSettingKey.RESERVATIONS_ENABLED),
    ),
    reservations_auto_confirm: parseBoolean(
      getSetting(settings, RestaurantSettingKey.RESERVATIONS_AUTO_CONFIRM),
    ),
    reservations_time_slot_minutes: parseNumber(
      getSetting(settings, RestaurantSettingKey.RESERVATIONS_TIME_SLOT_MINUTES),
      60,
    ),
    reservations_hide_duration_to_guests: parseBoolean(
      getSetting(
        settings,
        RestaurantSettingKey.RESERVATIONS_HIDE_DURATION_TO_GUESTS,
      ),
    ),
    reservations_time_slot_gap_minutes: parseNumber(
      getSetting(
        settings,
        RestaurantSettingKey.RESERVATIONS_TIME_SLOT_GAP_MINUTES,
      ),
      15,
    ),
    reservations_max_advance_booking_days: parseNumber(
      getSetting(settings, RestaurantSettingKey.RESERVATIONS_MAX_ADVANCE_DAYS),
      30,
    ),
    reservations_min_advance_booking_hours: parseNumber(
      getSetting(settings, RestaurantSettingKey.RESERVATIONS_MIN_ADVANCE_HOURS),
      2,
    ),
    reservations_min_party_size: parseNumber(
      getSetting(settings, RestaurantSettingKey.RESERVATIONS_MIN_PARTY_SIZE),
      1,
    ),
    reservations_max_party_size: parseNumber(
      getSetting(settings, RestaurantSettingKey.RESERVATIONS_MAX_PARTY_SIZE),
      12,
    ),
    reservations_services: parseReservationServices(
      getSetting(settings, RestaurantSettingKey.RESERVATIONS_SERVICES),
    ) as ReservationServicesSettings,
    reservation_schedule: parseReservationSchedule(
      getSetting(settings, RestaurantSettingKey.RESERVATION_SCHEDULE),
    ) as ReservationSchedule,
    reservations_note:
      (getSetting(
        settings,
        RestaurantSettingKey.RESERVATIONS_NOTE,
      ) as string) || "",
    reservations_confirmation_message:
      (getSetting(
        settings,
        RestaurantSettingKey.RESERVATIONS_CONFIRMATION_MESSAGE,
      ) as string) || "",
    reservations_cancellation_policy:
      (getSetting(
        settings,
        RestaurantSettingKey.RESERVATIONS_CANCELLATION_POLICY,
      ) as string) || "",
    reservations_rejection_message:
      (getSetting(
        settings,
        RestaurantSettingKey.RESERVATIONS_REJECTION_MESSAGE,
      ) as string) || "",
    reservations_require_deposit: parseBoolean(
      getSetting(settings, RestaurantSettingKey.RESERVATIONS_REQUIRE_DEPOSIT),
    ),
    reservations_deposit_amount: parseNumber(
      getSetting(settings, RestaurantSettingKey.RESERVATIONS_DEPOSIT_AMOUNT),
      0,
    ),
    reservations_cancellation_deadline_hours: parseNumber(
      getSetting(
        settings,
        RestaurantSettingKey.RESERVATIONS_CANCELLATION_DEADLINE_HOURS,
      ),
      24,
    ),
    reservation_algorithm:
      (getSetting(settings, RestaurantSettingKey.RESERVATION_ALGORITHM) as
        | "table_based"
        | "fixed_amount") || "table_based",
    review_request_enabled: parseBoolean(
      getSetting(settings, RestaurantSettingKey.REVIEW_REQUEST_ENABLED),
    ),
    review_request_delay_hours: parseNumber(
      getSetting(settings, RestaurantSettingKey.REVIEW_REQUEST_DELAY_HOURS),
      3,
    ),
    review_request_message:
      (getSetting(
        settings,
        RestaurantSettingKey.REVIEW_REQUEST_MESSAGE,
      ) as string) || "",
    review_request_min_days_between: parseNumber(
      getSetting(
        settings,
        RestaurantSettingKey.REVIEW_REQUEST_MIN_DAYS_BETWEEN,
      ),
      30,
    ),
    menu_pdfs: parseMenuPdfs(
      getSetting(settings, RestaurantSettingKey.MENU_PDFS),
    ) as MenuPdfItem[],
  };
};

type FormState = ReturnType<typeof parseSettings>;

const serializeSettings = (state: FormState) => JSON.stringify(state);

export const ReservationSettings = defineComponent({
  name: "ReservationSettings",
  setup() {
    const auth = useAuth();
    const { t } = useLocalization();
    const startTime = ref("09:00");
    const endTime = ref("22:00");
    const menuPdfAssetLifecycle = createMenuPdfAssetLifecycle(api.deleteAsset);
    const sections = computed<SectionedBodySection[]>(() => [
      {
        id: "general",
        label: t("settings.reservations.section_general"),
        icon: "settings",
      },
      {
        id: "booking-rules",
        label: t("settings.reservations.section_booking_rules"),
        icon: "event",
      },
      {
        id: "custom-experiences",
        label: t("settings.reservations.section_custom_experiences"),
        icon: "label",
      },
      {
        id: "service-periods",
        label: t("settings.reservations.section_service_periods"),
        icon: "restaurant",
      },
      {
        id: "customer-messaging",
        label: t("settings.reservations.section_customer_messaging"),
        icon: "message",
      },
      {
        id: "payment-deposits",
        label: t("settings.reservations.section_payment_deposits"),
        icon: "payments",
      },
      {
        id: "google-reviews",
        label: t("settings.reservations.section_google_reviews"),
        icon: "star",
      },
      {
        id: "review-requests",
        label: t("settings.reservations.section_review_requests"),
        icon: "rate_review",
      },
      {
        id: "menu-pdfs",
        label: t("settings.reservations.section_menu_pdfs"),
        icon: "picture_as_pdf",
      },
    ]);

    const {
      loading,
      saving,
      errorMessage,
      state: settings,
      isDirty,
      loadSettings: loadSettingsBase,
      resetForm,
      handleSave,
    } = useSettingsForm<FormState>({
      initialState: {
        reservations_enabled: true,
        reservations_auto_confirm: true,
        reservations_time_slot_minutes: 60,
        reservations_hide_duration_to_guests: false,
        reservations_time_slot_gap_minutes: 0,
        reservations_max_advance_booking_days: 30,
        reservations_min_advance_booking_hours: 2,
        reservations_min_party_size: 1,
        reservations_max_party_size: 12,
        reservations_services: { lunch: true, dinner: true },
        reservation_schedule: {},
        reservations_note: "",
        reservations_confirmation_message: "",
        reservations_cancellation_policy: "",
        reservations_rejection_message: "",
        reservations_require_deposit: false,
        reservations_deposit_amount: 0,
        reservations_cancellation_deadline_hours: 24,
        reservation_algorithm: "table_based",
        review_request_enabled: false,
        review_request_delay_hours: 3,
        review_request_message: "",
        review_request_min_days_between: 30,
        menu_pdfs: [],
      },
      serialize: serializeSettings,
      onLoad: async () => {
        const settingsResponse = await api.getSettings();
        const parsed = parseSettings(settingsResponse.data);

        // Load start/end times
        const startTimeSetting = settingsResponse.data.find(
          (s) => s.key === RestaurantSettingKey.START_TIME,
        );
        const endTimeSetting = settingsResponse.data.find(
          (s) => s.key === RestaurantSettingKey.END_TIME,
        );
        startTime.value = (startTimeSetting?.value as string) || "09:00";
        endTime.value = (endTimeSetting?.value as string) || "22:00";

        return parsed;
      },
      onSave: async (state) => {
        console.log("onSave", state);
        const settingsToUpdate = [
          createSetting(
            RestaurantSettingKey.RESERVATIONS_ENABLED,
            state.reservations_enabled,
            "boolean",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_AUTO_CONFIRM,
            state.reservations_auto_confirm,
            "boolean",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_TIME_SLOT_MINUTES,
            state.reservations_time_slot_minutes,
            "number",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_HIDE_DURATION_TO_GUESTS,
            state.reservations_hide_duration_to_guests,
            "boolean",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_TIME_SLOT_GAP_MINUTES,
            state.reservations_time_slot_gap_minutes,
            "number",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_MAX_ADVANCE_DAYS,
            state.reservations_max_advance_booking_days,
            "number",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_MIN_ADVANCE_HOURS,
            state.reservations_min_advance_booking_hours,
            "number",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_MIN_PARTY_SIZE,
            state.reservations_min_party_size,
            "number",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_MAX_PARTY_SIZE,
            state.reservations_max_party_size,
            "number",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_SERVICES,
            state.reservations_services,
            "json",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATION_SCHEDULE,
            state.reservation_schedule,
            "json",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_NOTE,
            state.reservations_note,
            "string",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_CONFIRMATION_MESSAGE,
            state.reservations_confirmation_message,
            "string",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_CANCELLATION_POLICY,
            state.reservations_cancellation_policy,
            "string",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_REJECTION_MESSAGE,
            state.reservations_rejection_message,
            "string",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_REQUIRE_DEPOSIT,
            state.reservations_require_deposit,
            "boolean",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_DEPOSIT_AMOUNT,
            state.reservations_deposit_amount,
            "number",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATIONS_CANCELLATION_DEADLINE_HOURS,
            state.reservations_cancellation_deadline_hours,
            "number",
          ),
          createSetting(
            RestaurantSettingKey.RESERVATION_ALGORITHM,
            state.reservation_algorithm,
            "string",
          ),
          createSetting(
            RestaurantSettingKey.REVIEW_REQUEST_ENABLED,
            state.review_request_enabled,
            "boolean",
          ),
          createSetting(
            RestaurantSettingKey.REVIEW_REQUEST_DELAY_HOURS,
            state.review_request_delay_hours,
            "number",
          ),
          createSetting(
            RestaurantSettingKey.REVIEW_REQUEST_MESSAGE,
            state.review_request_message,
            "string",
          ),
          createSetting(
            RestaurantSettingKey.REVIEW_REQUEST_MIN_DAYS_BETWEEN,
            state.review_request_min_days_between,
            "number",
          ),
          createSetting(
            RestaurantSettingKey.MENU_PDFS,
            state.menu_pdfs,
            "json",
          ),
        ];

        console.log("settingsToUpdate", settingsToUpdate);
        await api.bulkUpdateSettings({ settings: settingsToUpdate }, auth);

        // Reload and return updated settings
        const response = await api.getSettings();
        const parsed = parseSettings(response.data);
        await menuPdfAssetLifecycle.completeSave(parsed.menu_pdfs);

        return parsed;
      },
    });

    const updateSetting: ReservationSettingsUpdateHandler = (field, value) => {
      (settings.value as Record<string, unknown>)[field] = value;
    };

    const updateSchedule = (schedule: ReservationSchedule) => {
      settings.value.reservation_schedule = schedule;
    };

    const updateMenuPdfs = (items: MenuPdfItem[]) => {
      menuPdfAssetLifecycle.trackUpdate(settings.value.menu_pdfs, items);
      settings.value.menu_pdfs = items;
    };

    const registerUploadedMenuPdfAsset = (assetId: string) => {
      menuPdfAssetLifecycle.registerUpload(assetId);
    };

    const handleReset = async () => {
      try {
        await menuPdfAssetLifecycle.reset();
        resetForm();
      } catch (error) {
        console.error("Failed to clean up pending menu PDF uploads", error);
        errorMessage.value = t("settings.menu_pdfs.error_cleanup_failed");
      }
    };

    onMounted(() => {
      setTitle(t("settings.reservations.title"));
      loadSettingsBase();
    });

    onBeforeRouteLeave(async () => {
      if (saving.value) return false;

      try {
        await menuPdfAssetLifecycle.cleanupPendingUploads();
        return true;
      } catch (error) {
        console.error("Failed to clean up pending menu PDF uploads", error);
        errorMessage.value = t("settings.menu_pdfs.error_cleanup_failed");
        return false;
      }
    });

    onBeforeUnmount(() => {
      if (saving.value) return;
      void menuPdfAssetLifecycle.cleanupPendingUploads().catch((error) => {
        console.error("Failed to clean up pending menu PDF uploads", error);
      });
    });

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="event_available"
          title={t("settings.reservations.title")}
          description={t("settings.reservations.page_description")}
        >
          {{
            actions: () => (
              <>
                <button
                  type="button"
                  class="btn btn-ghost btn-sm"
                  disabled={!isDirty.value || saving.value}
                  onClick={handleReset}
                >
                  {t("general.words.reset")}
                </button>
                <button
                  type="button"
                  class="btn btn-primary btn-sm gap-2 shadow-lg hover:shadow-primary/25"
                  disabled={!isDirty.value || saving.value}
                  onClick={handleSave}
                >
                  {saving.value ? (
                    <span class="loading loading-spinner loading-sm"></span>
                  ) : (
                    <span class="material-symbols-outlined text-lg">save</span>
                  )}
                  <span class="hidden lg:flex">
                    {saving.value
                      ? t("general.words.saving")
                      : t("catalog.modals.save_changes")}
                  </span>
                </button>
              </>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent noPadding class="p-0 pb-4 lg:p-6">
            <div class="mx-auto w-full">
              {errorMessage.value && (
                <div class="alert alert-error mb-6 border border-error/30 bg-error/10 shadow-lg">
                  <span class="material-symbols-outlined">error</span>
                  <span>{errorMessage.value}</span>
                </div>
              )}

              {loading.value ? (
                <div class="flex min-h-[420px] items-center justify-center">
                  <div class="text-center">
                    <div class="relative inline-block">
                      <div class="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                      <span class="material-symbols-outlined text-primary text-2xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        event_available
                      </span>
                    </div>
                    <p class="mt-6 text-sm font-medium text-base-content/70">
                      {t("settings.reservations.loading_settings")}
                    </p>
                  </div>
                </div>
              ) : (
                <SectionedBody
                  sections={sections.value}
                  ariaLabel={t("settings.reservations.title")}
                >
                  {{
                    general: () => (
                      <ReservationSection
                        title={t("settings.reservations.section_general")}
                        icon="settings"
                      >
                        <GeneralSettings
                          enabled={settings.value.reservations_enabled}
                          autoConfirm={settings.value.reservations_auto_confirm}
                          onUpdate={updateSetting}
                        />
                      </ReservationSection>
                    ),
                    "booking-rules": () => (
                      <ReservationSection
                        title={t("settings.reservations.section_booking_rules")}
                        icon="event"
                      >
                        <BookingRules
                          mode={settings.value.reservation_algorithm}
                          maxGuests={settings.value.reservations_max_party_size}
                          defaultPeriodMinutes={
                            settings.value.reservations_time_slot_minutes
                          }
                          onUpdate={updateSetting}
                        />
                      </ReservationSection>
                    ),
                    "custom-experiences": () => (
                      <ReservationSection
                        title={t(
                          "settings.reservations.section_custom_experiences",
                        )}
                        icon="label"
                      >
                        <TagManagement />
                      </ReservationSection>
                    ),
                    "service-periods": () => (
                      <ReservationSection
                        title={t(
                          "settings.reservations.section_service_periods",
                        )}
                        icon="restaurant"
                      >
                        <ServicePeriods
                          schedule={settings.value.reservation_schedule}
                          startTime={startTime.value}
                          endTime={endTime.value}
                          onUpdate={updateSchedule}
                        />
                      </ReservationSection>
                    ),
                    "customer-messaging": () => (
                      <ReservationSection
                        title={t(
                          "settings.reservations.section_customer_messaging",
                        )}
                        icon="message"
                      >
                        <CustomerMessaging
                          reservationNote={settings.value.reservations_note}
                          confirmationMessage={
                            settings.value.reservations_confirmation_message
                          }
                          cancellationPolicy={
                            settings.value.reservations_cancellation_policy
                          }
                          rejectionMessage={
                            settings.value.reservations_rejection_message
                          }
                          hideDurationToGuests={
                            settings.value.reservations_hide_duration_to_guests
                          }
                          onUpdate={updateSetting}
                        />
                      </ReservationSection>
                    ),
                    "payment-deposits": () => (
                      <ReservationSection
                        title={t(
                          "settings.reservations.section_payment_deposits",
                        )}
                        icon="payments"
                      >
                        <PaymentDeposits
                          depositEnabled={
                            settings.value.reservations_require_deposit
                          }
                          depositAmount={
                            settings.value.reservations_deposit_amount
                          }
                          cancellationDeadlineHours={
                            settings.value
                              .reservations_cancellation_deadline_hours
                          }
                          onUpdate={updateSetting}
                        />
                      </ReservationSection>
                    ),
                    "google-reviews": () => (
                      <ReservationSection
                        title={t(
                          "settings.reservations.section_google_reviews",
                        )}
                        icon="star"
                      >
                        <GoogleReviews />
                      </ReservationSection>
                    ),
                    "review-requests": () => (
                      <ReservationSection
                        title={t(
                          "settings.reservations.section_review_requests",
                        )}
                        icon="rate_review"
                      >
                        <ReviewRequest
                          enabled={settings.value.review_request_enabled}
                          delayHours={settings.value.review_request_delay_hours}
                          minDaysBetween={
                            settings.value.review_request_min_days_between
                          }
                          customMessage={settings.value.review_request_message}
                          onUpdate={updateSetting}
                        />
                      </ReservationSection>
                    ),
                    "menu-pdfs": () => (
                      <ReservationSection
                        title={t("settings.reservations.section_menu_pdfs")}
                        icon="picture_as_pdf"
                      >
                        <MenuPdfs
                          items={settings.value.menu_pdfs}
                          onUpdate={updateMenuPdfs}
                          onAssetUploaded={registerUploadedMenuPdfAsset}
                        />
                      </ReservationSection>
                    ),
                  }}
                </SectionedBody>
              )}
            </div>
          </AppSubpageContent>
        </AppSubpage>
      </div>
    );
  },
});
